'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Building2, Wallet, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

type UserRole = 'investor' | 'developer' | 'inspector';

interface SignupFormData {
  firstName: string;
  lastName: string;
  email: string;
  organization: string;
  country: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

const roles = [
  {
    value: 'investor' as UserRole,
    label: 'Investor',
    description: 'Fund escrow and approve releases',
    icon: Wallet,
    color: 'border-primary/50 bg-primary/10 text-primary',
  },
  {
    value: 'developer' as UserRole,
    label: 'Developer',
    description: 'Create projects and milestones',
    icon: Building2,
    color: 'border-info/50 bg-info/10 text-info',
  },
  {
    value: 'inspector' as UserRole,
    label: 'Inspector',
    description: 'Verify milestone completion',
    icon: ClipboardCheck,
    color: 'border-warning/50 bg-warning/10 text-warning',
  },
];

interface SignupFormProps {
  onSwitchToLogin: () => void;
}

export default function SignupForm({ onSwitchToLogin }: SignupFormProps) {
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignupFormData>();

  const password = watch('password');

  const onSubmit = async (data: SignupFormData) => {
    if (!selectedRole) {
      toast.error('Please select your role before continuing');
      return;
    }
    setIsLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/signup with { ...data, role: selectedRole }
    await new Promise((r) => setTimeout(r, 1400));
    setIsLoading(false);
    setSubmitted(true);
    toast.success('Account created — check your email to verify');
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="w-14 h-14 rounded-full bg-success/10 border border-success/30 flex items-center justify-center mx-auto mb-4">
          <div className="w-6 h-6 rounded-full bg-success/30 flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-success" />
          </div>
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">Verify your email</h2>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          A verification link has been sent to your email address. Your account will be activated after verification and admin review.
        </p>
        <button
          onClick={onSwitchToLogin}
          className="text-sm text-primary hover:underline font-medium"
        >
          Return to sign in
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground mb-1">Create your account</h1>
        <p className="text-sm text-muted-foreground">Select your role to get started</p>
      </div>

      {/* Role Selection */}
      <div className="mb-6">
        <p className="text-sm font-medium text-foreground mb-3">I am registering as a</p>
        <div className="grid grid-cols-3 gap-2">
          {roles.map((role) => (
            <button
              key={`role-select-${role.value}`}
              type="button"
              onClick={() => setSelectedRole(role.value)}
              className={`p-3 rounded-lg border text-center transition-all duration-150 ${
                selectedRole === role.value
                  ? role.color
                  : 'border-border bg-card text-muted-foreground hover:border-primary/30 hover:text-foreground'
              }`}
            >
              <role.icon size={18} className="mx-auto mb-1" />
              <p className="text-xs font-semibold">{role.label}</p>
              <p className="text-xs opacity-70 hidden sm:block">{role.description}</p>
            </button>
          ))}
        </div>
        {!selectedRole && (
          <p className="mt-1 text-xs text-muted-foreground">Role selection required</p>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5" htmlFor="signup-first">
              First name
            </label>
            <input
              id="signup-first"
              type="text"
              className={`w-full px-3 py-2 rounded-md border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${errors.firstName ? 'border-danger' : 'border-border'}`}
              {...register('firstName', { required: 'Required' })}
            />
            {errors.firstName && <p className="mt-0.5 text-xs text-danger">{errors.firstName.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5" htmlFor="signup-last">
              Last name
            </label>
            <input
              id="signup-last"
              type="text"
              className={`w-full px-3 py-2 rounded-md border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${errors.lastName ? 'border-danger' : 'border-border'}`}
              {...register('lastName', { required: 'Required' })}
            />
            {errors.lastName && <p className="mt-0.5 text-xs text-danger">{errors.lastName.message}</p>}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-foreground mb-1.5" htmlFor="signup-email">
            Work email address
          </label>
          <input
            id="signup-email"
            type="email"
            className={`w-full px-3 py-2 rounded-md border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${errors.email ? 'border-danger' : 'border-border'}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
            })}
          />
          {errors.email && <p className="mt-0.5 text-xs text-danger">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-foreground mb-1.5" htmlFor="signup-org">
            Organization / Company
          </label>
          <input
            id="signup-org"
            type="text"
            className="w-full px-3 py-2 rounded-md border border-border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
            {...register('organization')}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-foreground mb-1.5" htmlFor="signup-country">
            Country of operation
          </label>
          <select
            id="signup-country"
            className="w-full px-3 py-2 rounded-md border border-border bg-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
            {...register('country', { required: 'Country is required' })}
          >
            <option value="">Select country</option>
            <option value="GH">Ghana</option>
            <option value="NG">Nigeria</option>
            <option value="KE">Kenya</option>
            <option value="ZA">South Africa</option>
            <option value="UK">United Kingdom</option>
            <option value="US">United States</option>
            <option value="AE">UAE</option>
            <option value="CA">Canada</option>
          </select>
          {errors.country && <p className="mt-0.5 text-xs text-danger">{errors.country.message}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-foreground mb-1.5" htmlFor="signup-password">
            Password
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters, one uppercase, one number</p>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              className={`w-full px-3 py-2 pr-10 rounded-md border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${errors.password ? 'border-danger' : 'border-border'}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Minimum 8 characters' },
                pattern: { value: /^(?=.*[A-Z])(?=.*\d)/, message: 'Must include uppercase and number' },
              })}
            />
            <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          {errors.password && <p className="mt-0.5 text-xs text-danger">{errors.password.message}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-foreground mb-1.5" htmlFor="signup-confirm">
            Confirm password
          </label>
          <div className="relative">
            <input
              id="signup-confirm"
              type={showConfirm ? 'text' : 'password'}
              className={`w-full px-3 py-2 pr-10 rounded-md border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${errors.confirmPassword ? 'border-danger' : 'border-border'}`}
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (v) => v === password || 'Passwords do not match',
              })}
            />
            <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowConfirm(!showConfirm)}>
              {showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="mt-0.5 text-xs text-danger">{errors.confirmPassword.message}</p>}
        </div>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="w-4 h-4 mt-0.5 rounded border-border bg-input accent-primary flex-shrink-0"
            {...register('acceptTerms', { required: 'You must accept the terms' })}
          />
          <span className="text-xs text-muted-foreground leading-relaxed">
            I agree to the{' '}
            <span className="text-primary hover:underline cursor-pointer">Terms of Service</span>
            {' '}and{' '}
            <span className="text-primary hover:underline cursor-pointer">Privacy Policy</span>
          </span>
        </label>
        {errors.acceptTerms && <p className="text-xs text-danger">{errors.acceptTerms.message}</p>}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 rounded-md text-sm font-semibold hover:bg-primary/90 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150"
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Creating account...
            </>
          ) : (
            'Create Account'
          )}
        </button>
      </form>

      <p className="mt-5 text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button onClick={onSwitchToLogin} className="text-primary hover:underline font-medium">
          Sign in
        </button>
      </p>
    </div>
  );
}